// ═══════════════════════════════════════════════════════════
// Jurisdiction & Exemption Codes API
// ═══════════════════════════════════════════════════════════

import { queryRecords, createRecord, updateRecord, fetchRecord } from './client';
import { TABLES } from './tables';
import { FIELDS } from './fields';

const FJ = FIELDS.jurisdictions;
const FE = FIELDS.exemptionCodes;

export interface Jurisdiction {
  recordId:               number;
  name:                   string;
  state:                  string;
  type:                   string;
  lawName:                string;
  statutoryDays:          number;
  extensionAllowed:       boolean;
  maxExtensionDays:       number;
  businessDaysOnly:       boolean;
  acknowledgmentRequired: boolean;
  acknowledgmentDays:     number;
  feesAllowed:            boolean;
  logoUrl?:               string;
  primaryColor?:          string;
  portalUrl?:             string;
  contactInfo?:           string;
  alertThresholdDays:     number;
}

export interface ExemptionCode {
  recordId:          number;
  jurisdictionId:    string;
  code:              string;
  title:             string;
  description:       string;
  statutoryCitation: string;
  isActive:          boolean;
}

function rowToJurisdiction(row: Record<string, unknown>): Jurisdiction {
  return {
    recordId:               row['record_id'] as number,
    name:                   String(row[FJ.name] ?? ''),
    state:                  String(row[FJ.state] ?? ''),
    type:                   String(row[FJ.type] ?? ''),
    lawName:                String(row[FJ.lawName] ?? 'FOIA'),
    statutoryDays:          Number(row[FJ.statutoryDays] ?? 20),
    extensionAllowed:       row[FJ.extensionAllowed] === true || row[FJ.extensionAllowed] === 'true',
    maxExtensionDays:       Number(row[FJ.maxExtensionDays] ?? 0),
    businessDaysOnly:       row[FJ.businessDaysOnly] !== false && row[FJ.businessDaysOnly] !== 'false',
    acknowledgmentRequired: row[FJ.acknowledgmentRequired] === true || row[FJ.acknowledgmentRequired] === 'true',
    acknowledgmentDays:     Number(row[FJ.acknowledgmentDays] ?? 5),
    feesAllowed:            row[FJ.feesAllowed] !== false && row[FJ.feesAllowed] !== 'false',
    logoUrl:                row[FJ.logoUrl] as string | undefined,
    primaryColor:           row[FJ.primaryColor] as string | undefined,
    portalUrl:              row[FJ.portalUrl] as string | undefined,
    contactInfo:            row[FJ.contactInfo] as string | undefined,
    alertThresholdDays:     Number(row[FJ.alertThresholdDays] ?? 5),
  };
}

function rowToExemption(row: Record<string, unknown>): ExemptionCode {
  return {
    recordId:          row['record_id'] as number,
    jurisdictionId:    String(row[FE.jurisdictionId] ?? ''),
    code:              String(row[FE.code] ?? ''),
    title:             String(row[FE.title] ?? ''),
    description:       String(row[FE.description] ?? ''),
    statutoryCitation: String(row[FE.statutoryCitation] ?? ''),
    isActive:          row[FE.isActive] !== false && row[FE.isActive] !== 'false',
  };
}

export async function getJurisdiction(recordId: number): Promise<Jurisdiction | null> {
  const row = await fetchRecord<Record<string, unknown>>(TABLES.jurisdictions, recordId);
  return row ? rowToJurisdiction(row) : null;
}

export async function listJurisdictions(opts?: { anonymous?: boolean }): Promise<Jurisdiction[]> {
  const { records } = await queryRecords(TABLES.jurisdictions, { limit: 100 }, opts);
  return (records as Record<string, unknown>[]).map(rowToJurisdiction);
}

export async function updateJurisdiction(
  recordId: number,
  patch: Partial<Omit<Jurisdiction, 'recordId'>>,
): Promise<Jurisdiction> {
  const row = await updateRecord(TABLES.jurisdictions, recordId, {
    ...(patch.name              !== undefined && { [FJ.name]:              patch.name }),
    ...(patch.statutoryDays     !== undefined && { [FJ.statutoryDays]:     patch.statutoryDays }),
    ...(patch.extensionAllowed  !== undefined && { [FJ.extensionAllowed]:  patch.extensionAllowed }),
    ...(patch.maxExtensionDays  !== undefined && { [FJ.maxExtensionDays]:  patch.maxExtensionDays }),
    ...(patch.businessDaysOnly  !== undefined && { [FJ.businessDaysOnly]:  patch.businessDaysOnly }),
    ...(patch.feesAllowed       !== undefined && { [FJ.feesAllowed]:       patch.feesAllowed }),
    ...(patch.alertThresholdDays!== undefined && { [FJ.alertThresholdDays]:patch.alertThresholdDays }),
    ...(patch.contactInfo       !== undefined && { [FJ.contactInfo]:       patch.contactInfo }),
    ...(patch.portalUrl         !== undefined && { [FJ.portalUrl]:         patch.portalUrl }),
    ...(patch.primaryColor      !== undefined && { [FJ.primaryColor]:      patch.primaryColor }),
    ...(patch.acknowledgmentRequired !== undefined && { [FJ.acknowledgmentRequired]: patch.acknowledgmentRequired }),
    ...(patch.acknowledgmentDays     !== undefined && { [FJ.acknowledgmentDays]:     patch.acknowledgmentDays }),
  });
  return rowToJurisdiction(row as Record<string, unknown>);
}

// ── Exemption Codes ──────────────────────────────────────

export async function listExemptionCodes(jurisdictionId: string): Promise<ExemptionCode[]> {
  const { records } = await queryRecords(TABLES.exemptionCodes, {
    filters: [{ field: FE.jurisdictionId, operator: 'eq', value: jurisdictionId }],
    limit: 200,
  });
  return (records as Record<string, unknown>[]).map(rowToExemption);
}

export async function createExemptionCode(
  input: Omit<ExemptionCode, 'recordId'>,
): Promise<ExemptionCode> {
  const row = await createRecord(TABLES.exemptionCodes, {
    [FE.jurisdictionId]:    input.jurisdictionId,
    [FE.code]:              input.code,
    [FE.title]:             input.title,
    [FE.description]:       input.description,
    [FE.statutoryCitation]: input.statutoryCitation,
    [FE.isActive]:          input.isActive,
  });
  return rowToExemption(row as Record<string, unknown>);
}

export async function updateExemptionCode(
  recordId: number,
  patch: Partial<Omit<ExemptionCode, 'recordId'>>,
): Promise<ExemptionCode> {
  const row = await updateRecord(TABLES.exemptionCodes, recordId, {
    ...(patch.code              !== undefined && { [FE.code]:              patch.code }),
    ...(patch.title             !== undefined && { [FE.title]:             patch.title }),
    ...(patch.description       !== undefined && { [FE.description]:       patch.description }),
    ...(patch.statutoryCitation !== undefined && { [FE.statutoryCitation]: patch.statutoryCitation }),
    ...(patch.isActive          !== undefined && { [FE.isActive]:          patch.isActive }),
  });
  return rowToExemption(row as Record<string, unknown>);
}

// Standard exemptions — seed on first setup
export const ILLINOIS_EXEMPTIONS: Omit<ExemptionCode, 'recordId' | 'jurisdictionId'>[] = [
  { code: '§4(a)',  title: 'Personnel Records — Privacy',          description: 'Information that would constitute a clearly unwarranted invasion of personal privacy', statutoryCitation: 'Public Records Act § 4(a)', isActive: true },
  { code: '§4(b)',  title: 'Private Information',                   description: 'Private information, unless disclosure is required by another provision of this Act', statutoryCitation: 'Public Records Act § 4(b)', isActive: true },
  { code: '§4(c)',  title: 'Personnel Records — Supervisory Review',description: 'Personnel files, records, information and reports', statutoryCitation: 'Public Records Act § 4(c)', isActive: true },
  { code: '§4(d)',  title: 'Law Enforcement Records',               description: 'Records in the possession of a law enforcement agency', statutoryCitation: 'Public Records Act § 4(d)', isActive: true },
  { code: '§4(e)',  title: 'Internal Communications',               description: 'Internal communications between a public body and an attorney', statutoryCitation: 'Public Records Act § 4(e)', isActive: true },
  { code: '§4(f)',  title: 'Deliberative Processes',                description: 'Deliberative communications within or among public bodies relating to policy formulation', statutoryCitation: 'Public Records Act § 4(f)', isActive: true },
  { code: '§4(g)',  title: 'Security Plans',                        description: 'Information relating to the security of public buildings', statutoryCitation: 'Public Records Act § 4(g)', isActive: true },
  { code: '§4(h)',  title: 'Personal Safety',                       description: 'Information that would endanger the life or physical safety of law enforcement personnel', statutoryCitation: 'Public Records Act § 4(h)', isActive: true },
  { code: '§4(n)',  title: 'Settlements — Ongoing Litigation',      description: 'Communications between public body and attorney during pending litigation', statutoryCitation: 'Public Records Act § 4(n)', isActive: true },
  { code: '§4(p)',  title: 'Financial Records',                     description: 'Financial and commercial information received in confidence', statutoryCitation: 'Public Records Act § 4(p)', isActive: true },
];

export async function seedExemptionCodes(jurisdictionId: string): Promise<void> {
  const existing = await listExemptionCodes(jurisdictionId);
  const existingCodes = new Set(existing.map(e => e.code));
  for (const ex of ILLINOIS_EXEMPTIONS) {
    if (existingCodes.has(ex.code)) continue;
    await createExemptionCode({ ...ex, jurisdictionId });
  }
}
